import { observable } from 'mobx';
import _ from 'lodash';

import { get, post } from 'services/request';

export default class AuctionFinalsStore {
  @observable objects = []; // objects = [ { id, name } ]
  @observable buyers = []; // buyers = [ { id, name, offers: [ { id, price, preference }] }]
  @observable selection = {}; // selection[<object id>] = <buyer id>

  @observable awardedCount = 0;
  @observable awardedPrice = 0;

  @observable selectedCount = 0;
  @observable selectedPrice = 0;

  @observable results = {}; // { succeeded: [ { saleId, buyerId }], failed: [ {saleId, buyerId, error }] }
  @observable processed = 0; // successfully awarded offers (progress report)

  // sale: SaleStore
  constructor(sale) {
    this.sale = sale;
    this.objects = observable([]);
    this.buyers = observable([]);
    this.selection = observable({});
    this.results = observable({
      succeeded: [],
      failed: [],
    });
  }

  recalculate = () => {
    this.awardedCount = 0;
    this.awardedPrice = 0;

    // assign buyer.awarded
    this.buyers.forEach((buyer) => {
      const awarded = _.find(buyer.offers, { awarded: true });
      if (awarded) {
        buyer.awarded = true;
        this.awardedCount++;
        this.awardedPrice += awarded.price;
      }
    });

    // calculate the highest offer for each property
    this.objects.forEach((object) => {
      // get all offers for the current property and sort them
      let objectOffers = _.map(this.buyers, (buyer) => {
        const objectOffer = _.find(
          buyer.offers,
          (offer) => offer.id === object.id
        );
        if (objectOffer)
          return Object.assign(objectOffer, { buyerId: buyer.id });
        return null;
      });
      objectOffers = _.filter(objectOffers, (offer) => offer !== null);
      objectOffers = _.sortBy(objectOffers, (offer) => -offer.price); // DESC
      object.offers = objectOffers;

      const highestOffer = _.find(object.offers, (offer) => !offer.awarded);
      object.highestBuyerId = highestOffer ? highestOffer.buyerId : null;

      object.awarded =
        typeof _.find(object.offers, (offer) => offer.awarded) !== 'undefined';
    });

    // calculate position of each offer from each buyer
    this.buyers.forEach((buyer) => {
      buyer.offers.forEach((offer) => {
        const _object = _.find(this.objects, (o) => o.id === offer.id);
        const _nonAwaredOffers = _.filter(
          _object.offers,
          (offer) => !offer.awarded
        );
        offer.position = _.findIndex(
          _nonAwaredOffers,
          (offer) => offer.buyerId === buyer.id
        );
        offer.count = _nonAwaredOffers.length;
      });

      const nonAwaredOffers = _.filter(buyer.offers, (offer) => !offer.awarded);
      buyer.preferredOffer = _.minBy(
        nonAwaredOffers,
        (_offer) => _offer.preference
      );
    });

    //console.log('objects', this.objects);
    //console.log('buyers', this.buyers);
  };

  calculateTotals = () => {
    this.selectedCount = 0;
    this.selectedPrice = 0;
    Object.keys(this.selection).forEach((objectId) => {
      const buyerId = this.selection[objectId];
      const buyer = _.find(this.buyers, (buyer) => buyer.id === buyerId);
      if (buyer) {
        const offer = _.find(buyer.offers, (offer) => offer.id === objectId);
        if (offer) {
          this.selectedCount++;
          this.selectedPrice += offer.price;
        }
      }
    });
  };

  get selected() {
    return Object.keys(this.selection).length;
  }

  isBuyerSelected = (buyerId) => {
    return _.find(this.selection, (_buyerId) => _buyerId === buyerId);
  };

  isObjectSelected = (objectId) => {
    return typeof this.selection[objectId] !== 'undefined';
  };

  select = (objectId, buyerId, notify = true) => {
    const object = _.find(this.objects, { id: objectId });
    const buyer = _.find(this.buyers, { id: buyerId });
    if ((object && object.awarded) || (buyer && buyer.awarded)) return; // no interaction allowed on awarded

    if (this.selection[objectId] === buyerId) delete this.selection[objectId];
    else {
      Object.keys(this.selection).forEach((key) => {
        if (this.selection[key] === buyerId) delete this.selection[key]; // deselect another offer
      });
      this.selection[objectId] = buyerId; // select this offer
    }
    if (notify) this.selectionChanged();
  };

  clearSelection = () => {
    this.selection = observable({});
    this.selectionChanged();
  };

  selectionChanged = () => {
    // calculate the highest offer for each property
    this.objects.forEach((object) => {
      object.selected = this.selection[object.id];
    });

    // calculate position of each offer from each buyer
    this.buyers.forEach((buyer) => {
      buyer.selected = _.findKey(
        this.selection,
        (buyerId) => buyerId === buyer.id
      );
    });

    this.calculateTotals(); // recalculate total price

    if (this.onSelectionChanged) this.onSelectionChanged(this.selection);
  };

  load = async (abortSignal = null) => {
    this.objects.clear();
    this.buyers.clear();

    const sales = await get(
      `/api/v1/package-auctions/${this.sale.saleId}/sales`,
      this.sale.user.accessToken,
      null,
      abortSignal
    );

    const sortedSales = _.sortBy(sales, (sale) => sale.attributes.title);

    sortedSales.forEach((sale) => {
      this.objects.push({
        id: sale.saleId,
        name: sale.attributes.number,
        title: sale.attributes.title,
        address: sale.attributes.object_address,
      });
    });

    const offers = await get(
      `/api/v1/package-auctions/${this.sale.saleId}/offers`,
      this.sale.user.accessToken,
      null,
      abortSignal
    );

    if (abortSignal != null && abortSignal.aborted) return;

    const sortedOffers = _.sortBy(offers, (offer) => offer.submittedAt);

    const filteredOffers = _.filter(
      sortedOffers,
      (offer) => offer.status === 'confirmed' || offer.status === 'awarded'
    );

    filteredOffers.forEach((offer) => {
      let buyer = _.find(this.buyers, (b) => b.id === offer.buyerId);
      if (!buyer) {
        buyer = {
          id: offer.buyerId,
          name: `${offer.buyer.buyer_firstName} ${offer.buyer.buyer_lastName}`,
          offers: [
            {
              id: offer.saleId,
              price: !!offer.data.units
                ? offer.submittedValue * offer.data.units
                : offer.submittedValue,
              preference: 1,
              awarded: offer.status === 'awarded',
            },
          ],
        };
        this.buyers.push(buyer);
      } else {
        buyer.offers.push({
          id: offer.saleId,
          price: !!offer.data.units
            ? offer.submittedValue * offer.data.units
            : offer.submittedValue,
          preference: buyer.offers.length + 1,
          awarded: offer.status === 'awarded',
        });
      }
    });

    this.recalculate();
  };

  award = async (abortSignal = null) => {
    const objectIds = Object.keys(this.selection);

    this.processed = 0;
    this.results.succeeded.clear();
    this.results.failed.clear();

    for (let i = 0; i < objectIds.length; i++) {
      const selectedObjectId = objectIds[i];
      const selectedBuyerId = this.selection[selectedObjectId];

      const buyer = _.find(
        this.buyers,
        (buyer) => buyer.id === selectedBuyerId
      );
      const offer = buyer
        ? _.find(buyer.offers, (offer) => offer.id === selectedObjectId)
        : null;

      try {
        await post(
          `/api/v1/wizard/${selectedObjectId}/award`,
          {
            rejectOffers: [],
            buyerId: selectedBuyerId,
            message: '',
          },
          this.sale.user.accessToken,
          abortSignal
        );
        if (abortSignal != null && abortSignal.aborted) return;

        this.results.succeeded.push({
          saleId: selectedObjectId,
          buyerId: selectedBuyerId,
        });
        if (offer) offer.awarded = true;
      } catch (error) {
        this.results.failed.push({
          saleId: selectedObjectId,
          buyerId: selectedBuyerId,
          error,
        });
      }

      this.processed = this.processed + 1;
    }
  };

  awardAll = async (abortSignal = null) => {
    this.processed = 0;
    this.results.succeeded.clear();
    this.results.failed.clear();

    const payload = _.map(Object.keys(this.selection), (objectId) => {
      return {
        saleId: objectId,
        rejectOffers: [],
        buyerId: this.selection[objectId],
        message: '',
      };
    });

    try {
      const response = await post(
        `/api/v1/wizard/awardall`,
        payload,
        this.sale.user.accessToken,
        abortSignal
      );
      if (abortSignal != null && abortSignal.aborted) return;

      this.processed = this.processed + this.selectedCount;

      //console.log(response);

      if (response && response.results) {
        for (var i = 0; i < payload.length; i++) {
          if (response.results.length <= i) break;
          const result = response.results[i];
          if (result.successful) {
            this.results.succeeded.push({
              saleId: payload[i].saleId,
              buyerId: payload[i].buyerId,
            });
            const buyer = _.find(this.buyers, { id: payload[i].buyerId });
            if (buyer) {
              const offer = _.find(buyer.offers, { id: payload[i].saleId });
              if (offer) offer.awarded = true;
            }
          } else {
            this.results.failed.push({
              saleId: payload[i].saleId,
              buyerId: payload[i].buyerId,
              error: result.error,
            });
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // remove awarded offers from selection
  removeAwarded = () => {
    const objectIds = Object.keys(this.selection);
    objectIds.forEach((objectId) => {
      const buyer = _.find(
        this.buyers,
        (buyer) => buyer.id === this.selection[objectId]
      );
      if (buyer) {
        const offer = _.find(buyer.offers, (offer) => offer.id === objectId);
        if (offer && offer.awarded) delete this.selection[objectId];
      }
    });
    this.recalculate();
    this.selectionChanged();
  };
}



// WEBPACK FOOTER //
// ./src/app/components/AuctionFinals/stores/AuctionFinalsStore.js